import { AsyncRenderer } from '@client/new/components/AsyncRenderer/AsyncRenderer';
import {
  AllEmptyValue,
  WorkoutNotification,
  WorkoutsViewModel,
  listTitles,
  useWorkoutsViewModel,
} from './workouts.viewModel';
import { faDumbbell } from '@fortawesome/pro-solid-svg-icons';
import { ToggleButtonGroup } from '@client/new/components/ToggleButtonGroup/ToggleButtonGroup';
import { UseWorkoutsModelParams } from './workouts.model';
import {
  Alert,
  Box,
  Snackbar,
  Typography as MuiTypography,
} from '@mui/material';
import { EmptyState } from '@client/new/components/EmptyState/EmptyState';
import { NewProgramCard } from '@client/new/components/NewProgramCard/NewProgramCard';
import { BasicMenu } from '@client/new/components/BasicMenu/BasicMenu';
import { useTabHash } from '@client/new/hooks/useTabHash';
import { WorkoutType } from './workouts.domain';
import { useEffect } from 'react';
import { keys } from '../../../helpers/general';
import { SmartTable } from '../../../components/SmartTable/SmartTable';
import { colors } from '@client/new/components/colors';
import { Link } from 'react-router';
import { workoutTabToType } from './workouts.viewModel.tables';
import { SearchInput } from '@design-system/components/searchinput';
import { Typography } from '@design-system/components/typography/typography';
import { Button } from '@design-system/components/button/button';
import { FlowEducationalModal } from '@client/new/common/onboardingFlow/FlowEducationalModal';

export const AsyncWorkouts = (p: UseWorkoutsModelParams) => {
  const workoutsViewModel = useWorkoutsViewModel(p);
  return <AsyncRenderer data={workoutsViewModel} renderer={Workouts} />;
};

export const Workouts = (p: WorkoutsViewModel) => {
  const render = () => {
    switch (p.mode.type) {
      case 'all-empty':
        return <EmptyWorkoutsList {...p.mode.value} />;

      case 'selected-empty':
        return (
          <WorkoutsLists {...p}>
            <MuiTypography variant="h6" sx={{ color: '#8F94A1' }}>
              This list is empty. All {listTitles[p.selectedList]} workouts will
              appear here.
            </MuiTypography>
          </WorkoutsLists>
        );

      case 'standard':
        return (
          <WorkoutsLists {...p}>
            <SmartTable {...p.mode.value} />
          </WorkoutsLists>
        );
    }
  };
  return <div className="flex flex-col gap-4">{render()}</div>;
};

const EmptyWorkoutsList = ({
  createClassURL,
  createWorkoutURL,
  educationalModal,
}: AllEmptyValue) => (
  <Box py={5}>
    <EmptyState
      icon={faDumbbell}
      captionLine1="Create some workouts by choosing workout type."
    >
      <div className="flex flex-col gap-6">
        <div className="flex gap-4 max-w-[520px]">
          <NewProgramCard
            text="Workout consists mostly of looping 5-second clips that are great for self-guided exercises."
            buttonText="New workout"
            to={createWorkoutURL}
          />
          <NewProgramCard
            text="Class is a long-form video which includes guided workouts like HIIT, Yoga, and Pilates"
            buttonText="New class"
            to={createClassURL}
          />
        </div>
        <Button
          variant="text"
          color="neutral"
          onClick={educationalModal.onClose}
        >
          <Typography
            variant="body"
            size="medium"
            className="underline text-center text-black"
          >
            Learn how workouts fit into your program
          </Typography>
        </Button>
      </div>
    </EmptyState>
    <FlowEducationalModal {...educationalModal} />
  </Box>
);

const Nofication = (
  p: WorkoutNotification & Pick<WorkoutsViewModel, 'hideNotification'>,
): JSX.Element => {
  switch (p.type) {
    case 'duplicate':
      return (
        <Snackbar
          open
          key={p.type}
          onClose={() => p.hideNotification(p.type)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          autoHideDuration={4000}
        >
          <Alert
            severity={p.success ? 'success' : 'error'}
            sx={{
              background: p.success
                ? colors.successAlertBg
                : colors.warnAlertBg,
              boxShadow: '0px 3px 8px 0px #1D293914, 0px 0px 1px 0px #EAECF0',
            }}
          >
            {p.success ? (
              <div className="flex justify-between items-center gap-8">
                <span>Workout duplicated successfully.</span>
                <Link to={p.url ?? ''}>
                  <MuiTypography
                    variant="body1"
                    color="CaptionText"
                    sx={{ textDecoration: 'underline' }}
                  >
                    See in drafts
                  </MuiTypography>
                </Link>
              </div>
            ) : (
              <span>Workout duplication failed.</span>
            )}
          </Alert>
        </Snackbar>
      );
  }
};

const WorkoutsLists: React.FC<React.PropsWithChildren<WorkoutsViewModel>> = (
  p,
) => {
  const { tabHash, onTabChange } = useTabHash(
    keys(workoutTabToType),
    'workouts-published',
  );

  useEffect(() => {
    p.dispatch({
      type: 'selectList',
      payload: workoutTabToType[tabHash],
    });
    // only on component mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {p.notifications.map((n) => (
        <Nofication key={n.type} {...n} hideNotification={p.hideNotification} />
      ))}

      <div className="flex items-center justify-between">
        <ToggleButtonGroup<WorkoutType>
          toStringValue={(v) => `${v}`}
          toValue={(v) => v as WorkoutsViewModel['selectedList']}
          value={workoutTabToType[tabHash]}
          options={p.listOptions}
          onChange={(e, payload) => {
            p.dispatch({ type: 'selectList', payload });
            onTabChange(e, `workouts-${payload}`);
          }}
          newStyle={true}
        />

        <BasicMenu {...p.menuProps} variant="dark" disabled={p.disableMenu} />
      </div>

      <div className="w-[312px]">
        <SearchInput
          value={p.searchText}
          placeholder="Search workout"
          onChange={(e) => p.setSearchText(e.target.value)}
          onClear={() => p.setSearchText('')}
        />
      </div>
      {p.hasUnassignedWorkouts && (
        <Alert severity="warning" sx={{ background: colors.warnAlertBg }}>
          Weeks are not set. Please choose week & day for each workout
        </Alert>
      )}
      <>{p.children}</>
    </>
  );
};
